// src/BookingPage.js
import React, { useEffect, useState, useRef } from "react";
import { db } from "./firebase";
import { getClientEmail, setClientAccount as persistClientAccount, findAccountByEmail } from "./clientIdentity";
import { format, addMinutes,parseISO, isSameWeek,startOfWeek, addWeeks, addDays } from "date-fns";
import {
  collection, runTransaction, doc, addDoc, serverTimestamp, query, where,
} from "firebase/firestore";
import { getDoc, getDocs, onSnapshot } from "./queryMonitor"; // timed reads (Billing → Performance)
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./components/ui/dialog";

// Keep in sync with the default in Settings.js
const DEFAULT_SLOT_REQUEST_OPTIONS = ["Mondays before 16h", "Thursdays before 15h"];

function BookingPage() {
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingType, setBookingType] = useState("solo"); // "solo" | "duo"
  const [dancers, setDancers] = useState([
    { name: "", surname: "", studentId: "" },
    { name: "", surname: "", studentId: "" },
  ]);
  const [suggestions, setSuggestions] = useState([[], []]);
  const [showSuggestions, setShowSuggestions] = useState([false, false]);
  const [knownStudents, setKnownStudents] = useState([]);
  const studentsLoaded = useRef(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [collapsedDays, setCollapsedDays] = useState(new Set());
  const toggleDay = (date) => setCollapsedDays((prev) => {
    const next = new Set(prev);
    next.has(date) ? next.delete(date) : next.add(date);
    return next;
  });
  const [reason, setReason] = useState("");
  const [customReason, setCustomReason] = useState("");

  const DEFAULT_REASONS = ["Hip Hop", "Acro Dance", "Acro Sport", "Modern", "Contemp", "Lyrical", "Jazz", "Exam Prep", "Other"];
  const [CLASS_REASONS, setClassReasons] = useState(DEFAULT_REASONS);
  const [choreoEligibleStyles, setChoreoEligibleStyles] = useState([]);
  const [slotRequestOptions, setSlotRequestOptions] = useState([]);

  // Client identity — persisted in localStorage (set on the Home page)
  const [clientEmail] = useState(getClientEmail);
  const [clientAccount, setClientAccount] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dance_booking_account")); } catch { return null; }
  });
  const [weekOffset, setWeekOffset] = useState(0);
  const [minAdvanceHours, setMinAdvanceHours] = useState(0);
  const [maxAdvanceDays, setMaxAdvanceDays] = useState(14);

  // "Can't find a slot?" request
  const [showRequest, setShowRequest] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const EMPTY_REQUEST = { studentName: "", studentSurname: "", style: "", availability: [], needsContact: false, notes: "", contactName: "", contactPhone: "" };
  const [requestForm, setRequestForm] = useState(EMPTY_REQUEST);

  const myStudents = clientAccount
    ? knownStudents.filter((s) => s.accountId === clientAccount.id)
    : [];


  useEffect(() => {
    // Only the week on screen — not every slot ever made. Slot dates are
    // "YYYY-MM-DD" strings, so they compare in date order.
    const viewWeekStart = addWeeks(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset);
    const weekQuery = query(collection(db, "slots"),
      where("date", ">=", format(viewWeekStart, "yyyy-MM-dd")),
      where("date", "<=", format(addDays(viewWeekStart, 6), "yyyy-MM-dd")));
    const unsubscribe = onSnapshot(weekQuery, (snapshot) => {
    const allSlots = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

    const filtered = allSlots.filter((slot) => {
      const slotDate = parseISO(slot.date);
      return isSameWeek(slotDate, viewWeekStart, { weekStartsOn: 1 });
    });

    const sorted = filtered.sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.time}`);
      const dateB = new Date(`${b.date}T${b.time}`);
      return dateA - dateB;
    });

  setSlots(sorted);
      });

      return () => unsubscribe();
    }, [weekOffset]);

  useEffect(() => { setCollapsedDays(new Set()); }, [weekOffset]);


  useEffect(() => {
    const loadConfig = async () => {
      const [globalSnap, billingSnap] = await Promise.all([
        getDoc(doc(db, "config", "global")),
        getDoc(doc(db, "config", "billing")),
      ]);
      if (globalSnap.exists()) {
        const data = globalSnap.data();
        setMinAdvanceHours(data.minAdvanceHours ?? 8);
        setMaxAdvanceDays(data.maxAdvanceDays ?? 14);
      }
      if (billingSnap.exists()) {
        const data = billingSnap.data();
        if (data.classReasons?.length) setClassReasons(data.classReasons);
        setChoreoEligibleStyles(data.choreoEligibleStyles || []);
        // Absent = never configured, so fall back to defaults. An explicitly
        // saved empty list means "turn the request button off".
        setSlotRequestOptions(data.slotRequestOptions ?? DEFAULT_SLOT_REQUEST_OPTIONS);
      }
    };
    loadConfig();
  }, []);

  const loadStudentsOnce = () => {
    if (studentsLoaded.current) return;
    studentsLoaded.current = true;
    getDocs(collection(db, "students")).then((snap) => {
      setKnownStudents(snap.docs.map((d) => ({ id: d.id, name: d.data().name || "", surname: d.data().surname || "", accountId: d.data().accountId || "" })));
    });
  };

  // Retry account lookup on load if email is known but account wasn't found previously
  useEffect(() => {
    if (clientEmail && !clientAccount) {
      findAccountByEmail(clientEmail).then((acc) => {
        if (acc) {
          setClientAccount(acc);
          persistClientAccount(acc);
        }
      });
    }
  }, [clientEmail, clientAccount]);

  const updateDancer = (idx, field, val) => {
    setDancers((prev) => prev.map((d, i) => i === idx ? { ...d, [field]: val, studentId: "" } : d));
    const name = field === "name" ? val : dancers[idx].name;
    const surname = field === "surname" ? val : dancers[idx].surname;
    const q = `${name} ${surname}`.trim().toLowerCase();
    if (q.length < 2) {
      setSuggestions((prev) => prev.map((s, i) => i === idx ? [] : s));
      setShowSuggestions((prev) => prev.map((s, i) => i === idx ? false : s));
      return;
    }
    const matches = knownStudents.filter((st) => {
      const full = `${st.name} ${st.surname}`.toLowerCase();
      const rev = `${st.surname} ${st.name}`.toLowerCase();
      return full.includes(q) || rev.includes(q) || (name && st.name.toLowerCase().startsWith(name.toLowerCase())) || (surname && st.surname.toLowerCase().startsWith(surname.toLowerCase()));
    });
    // Sort account's own students to the top
    const accountId = clientAccount?.id;
    const sorted = accountId
      ? [...matches].sort((a, b) => (b.accountId === accountId ? 1 : 0) - (a.accountId === accountId ? 1 : 0))
      : matches;
    setSuggestions((prev) => prev.map((s, i) => i === idx ? sorted.slice(0, 8) : s));
    setShowSuggestions((prev) => prev.map((s, i) => i === idx ? matches.length > 0 : s));
  };

  const pickStudent = (idx, st) => {
    setDancers((prev) => prev.map((d, i) => i === idx ? { name: st.name, surname: st.surname, studentId: st.id } : d));
    setSuggestions((prev) => prev.map((s, i) => i === idx ? [] : s));
    setShowSuggestions((prev) => prev.map((s, i) => i === idx ? false : s));
  };

  const resetBookingForm = () => {
    setBookingType("solo");
    setDancers([{ name: "", surname: "", studentId: "" }, { name: "", surname: "", studentId: "" }]);
    setSuggestions([[], []]);
    setShowSuggestions([false, false]);
    setReason("");
    setCustomReason("");
  };


  const weekStart = addWeeks(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset);
  const weekEnd = addMinutes(addWeeks(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset + 1), -1);


  const handleBook = async () => {
    const d1 = dancers[0];
    const d2 = dancers[1];
    if (!selectedSlot) return;
    const isCustomClass = selectedSlot.classType === "Workshop" || selectedSlot.classType === "Custom";
    if (!d1.name || !d1.surname) return alert("Please fill in the dancer's name and surname.");
    if (!isCustomClass && bookingType === "duo" && (!d2.name || !d2.surname)) return alert("Please fill in both dancers' names for a duo booking.");
    if (!isCustomClass && !reason) return alert("Please select a reason for the class.");
    if (!isCustomClass && reason === "Other" && !customReason.trim()) return alert("Please describe your reason for the class.");

    const finalReason = reason === "Other" ? customReason.trim() : reason;
    const isDuo = !isCustomClass && bookingType === "duo";

    setIsBooking(true);
    try {
      const slotRef = doc(db, "slots", selectedSlot.id);
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(slotRef);
        if (!snap.exists()) throw new Error("This slot no longer exists.");
        const data = snap.data();
        const bookings = data.bookings || [];
        if (bookings.length >= data.capacity) throw new Error("Sorry, this slot just became fully booked.");
        const entry = isDuo
          ? { isDuo: true, dancer1: { name: d1.name, surname: d1.surname, studentId: d1.studentId || null }, dancer2: { name: d2.name, surname: d2.surname, studentId: d2.studentId || null }, reason: finalReason }
          : { name: d1.name, surname: d1.surname, reason: finalReason || null, studentId: d1.studentId || null };
        transaction.update(slotRef, { bookings: [...bookings, entry] });
      });
      // Booking succeeded — show confirmation immediately before doing anything else
      setConfirmedBooking(selectedSlot);
      setSelectedSlot(null);
      resetBookingForm();
      if (!isCustomClass) {
        try {
          await addDoc(collection(db, "pendingChoreoRequests"), {
            isDuo,
            studentName: d1.name.trim(),
            studentSurname: d1.surname.trim(),
            studentId: d1.studentId || null,
            ...(isDuo && { dancer2: { name: d2.name.trim(), surname: d2.surname.trim(), studentId: d2.studentId || null } }),
            style: finalReason,
            isChoreoEligible: choreoEligibleStyles.includes(finalReason),
            slotId: selectedSlot.id,
            slotDate: selectedSlot.date,
            slotTime: selectedSlot.time,
            slotType: selectedSlot.classType,
            status: "pending",
            createdAt: serverTimestamp(),
          });
        } catch (choreoErr) {
          console.error("pendingChoreoRequests write failed (booking still confirmed):", choreoErr);
        }
      }
    } catch (err) {
      alert(err.message || "Booking failed. Please check your connection and try again.");
    } finally {
      setIsBooking(false);
    }
  };



  const openRequest = () => {
    loadStudentsOnce();
    setRequestForm({
      ...EMPTY_REQUEST,
      contactName: clientAccount ? `${clientAccount.parentName || ""} ${clientAccount.parentSurname || ""}`.trim() : "",
      contactPhone: clientAccount?.phone || "",
    });
    setRequestSent(false);
    setShowRequest(true);
  };

  const handleSendRequest = async () => {
    const f = requestForm;
    if (!f.studentName.trim()) return alert("Please enter the dancer's first name.");
    if (f.availability.length === 0 && !f.needsContact) {
      return alert("Please select at least one time that would suit you, or choose \"None of these work\".");
    }
    if (!clientAccount && !f.contactName.trim()) return alert("Please enter your name so we can get back to you.");
    if (!clientAccount && !f.contactPhone.trim() && !clientEmail) return alert("Please enter a phone number so we can get back to you.");

    setSendingRequest(true);
    try {
      const matched = knownStudents.find(
        (s) => s.name.toLowerCase() === f.studentName.trim().toLowerCase()
            && s.surname.toLowerCase() === f.studentSurname.trim().toLowerCase()
      );
      await addDoc(collection(db, "slotRequests"), {
        accountId: clientAccount?.id || null,
        accountName: clientAccount
          ? `${clientAccount.parentName || ""} ${clientAccount.parentSurname || ""}`.trim()
          : f.contactName.trim(),
        accountEmail: clientAccount?.email || clientEmail || null,
        accountPhone: (clientAccount?.phone || f.contactPhone || "").trim() || null,
        studentName: f.studentName.trim(),
        studentSurname: f.studentSurname.trim(),
        studentId: matched?.id || null,
        style: f.style.trim() || null,
        availability: f.availability,
        needsContact: f.needsContact,
        notes: f.notes.trim() || null,
        status: "open",
        createdAt: serverTimestamp(),
      });
      setRequestSent(true);
    } catch (err) {
      console.error("slotRequests write failed:", err);
      alert("Could not send your request. Please check your connection and try again.");
    } finally {
      setSendingRequest(false);
    }
  };

  function buildGoogleCalendarUrl(slot) {
    const start = new Date(`${slot.date}T${slot.time}`);
    const end = addMinutes(start, slot.duration || 30);
    const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const title = encodeURIComponent(slot.customClassName || slot.classType);
    const location = encodeURIComponent(slot.location || "");
    const teacherLabel = slot.teachers?.length ? slot.teachers.join(" or ") : slot.teacher || "your teacher";
    const details = encodeURIComponent(`Dance class with ${teacherLabel}${slot.location ? ` at ${slot.location}` : ""}`);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(start)}/${fmt(end)}&details=${details}&location=${location}`;
  }

  function isSlotWithinBookingWindow(slotDate, slotTime) {
    const now = new Date();
    const slotDateTime = new Date(`${slotDate}T${slotTime}`);
    const diffMs = slotDateTime - now;
    const diffHours = diffMs / (1000 * 60 * 60);
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    if (diffHours < minAdvanceHours) {
      alert(`Bookings must be made at least ${minAdvanceHours} hours in advance.`);
      return false;
    }

    if (diffDays > maxAdvanceDays) {
      alert(`Bookings can't be made more than ${maxAdvanceDays} days in advance.`);
      return false;
    }

    return true;
  }


  return (
    <div style={{maxWidth:'680px', margin:'0 auto'}}>
      <div className="page-header">
        <h2 className="page-title">Book a Dance Lesson</h2>
      </div>

      {/* Can't find a slot? */}
      {slotRequestOptions.length > 0 && (
      <div
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap",
          border: "1px solid var(--color-border)",
          background: "color-mix(in oklch, var(--color-accent) 8%, var(--color-surface))",
          borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "1rem",
        }}
      >
        <span style={{ fontSize: "0.88rem" }}>
          <strong>Can't find what you're looking for?</strong>{" "}
          <span style={{ color: "var(--color-muted)" }}>Tell us when suits you and we'll try to open a slot.</span>
        </span>
        <Button size="sm" onClick={openRequest}>Book here</Button>
      </div>
      )}

      <Dialog open={showRequest} onOpenChange={(open) => { setShowRequest(open); if (!open) setRequestSent(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{requestSent ? "Request sent" : "Request a time"}</DialogTitle>
          </DialogHeader>

          {requestSent ? (
            <div style={{ fontSize: "0.9rem", lineHeight: 1.5 }}>
              <p style={{ marginTop: 0 }}>
                {requestForm.needsContact
                  ? "Thanks! A teacher will contact you to sort out a time that works."
                  : "Thanks! Your teachers can see your request and will get back to you if they can open a slot."}
              </p>
              <p style={{ color: "var(--color-muted)", margin: 0 }}>
                This doesn't reserve a lesson — keep an eye on the booking page in case a slot opens up.
              </p>
              <DialogFooter>
                <Button onClick={() => setShowRequest(false)}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="form-group">
                <Label>Dancer</Label>
                {myStudents.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem", marginBottom: "0.5rem" }}>
                    {myStudents.map((s) => {
                      const selected = requestForm.studentName === s.name && requestForm.studentSurname === s.surname;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setRequestForm((f) => ({ ...f, studentName: s.name, studentSurname: s.surname }))}
                          style={{
                            padding: "4px 10px", borderRadius: "999px", fontSize: "0.8rem", cursor: "pointer",
                            border: "1px solid var(--color-border)",
                            background: selected ? "var(--color-accent)" : "var(--color-surface)",
                            color: selected ? "var(--color-accent-text)" : "var(--color-text)",
                          }}
                        >
                          {s.name} {s.surname}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <Input
                    value={requestForm.studentName}
                    onChange={(e) => setRequestForm((f) => ({ ...f, studentName: e.target.value }))}
                    placeholder="First name *"
                    style={{ flex: 1 }}
                  />
                  <Input
                    value={requestForm.studentSurname}
                    onChange={(e) => setRequestForm((f) => ({ ...f, studentSurname: e.target.value }))}
                    placeholder="Surname"
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div className="form-group">
                <Label>What kind of class?</Label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem" }}>
                  {CLASS_REASONS.map((r) => {
                    const selected = requestForm.style === r;
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setRequestForm((f) => ({ ...f, style: selected ? "" : r }))}
                        style={{
                          padding: "4px 10px", borderRadius: "999px", fontSize: "0.8rem", cursor: "pointer",
                          border: "1px solid var(--color-border)",
                          background: selected ? "var(--color-accent)" : "var(--color-surface)",
                          color: selected ? "var(--color-accent-text)" : "var(--color-text)",
                        }}
                      >
                        {r}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="form-group">
                <Label>Which of these would suit you? *</Label>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", marginTop: "0.25rem" }}>
                  {slotRequestOptions.map((o) => {
                    const selected = requestForm.availability.includes(o);
                    return (
                      <label
                        key={o}
                        style={{
                          display: "flex", alignItems: "center", gap: "0.6rem", cursor: "pointer",
                          padding: "0.5rem 0.75rem", borderRadius: "8px",
                          border: `1px solid ${selected ? "var(--color-accent)" : "var(--color-border)"}`,
                          background: selected ? "color-mix(in oklch, var(--color-accent) 10%, var(--color-surface))" : "var(--color-surface)",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => setRequestForm((f) => ({
                            ...f,
                            needsContact: false,
                            availability: selected
                              ? f.availability.filter((x) => x !== o)
                              : [...f.availability, o],
                          }))}
                          style={{ width: "16px", height: "16px", accentColor: "var(--color-accent)", flexShrink: 0 }}
                        />
                        <span style={{ fontSize: "0.88rem" }}>{o}</span>
                      </label>
                    );
                  })}

                  <label
                    style={{
                      display: "flex", alignItems: "center", gap: "0.6rem", cursor: "pointer",
                      padding: "0.5rem 0.75rem", borderRadius: "8px", marginTop: "0.25rem",
                      border: `1px dashed ${requestForm.needsContact ? "var(--color-accent)" : "var(--color-border)"}`,
                      background: requestForm.needsContact
                        ? "color-mix(in oklch, var(--color-accent) 10%, var(--color-surface))"
                        : "transparent",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={requestForm.needsContact}
                      onChange={() => setRequestForm((f) => ({
                        ...f,
                        needsContact: !f.needsContact,
                        availability: !f.needsContact ? [] : f.availability,
                      }))}
                      style={{ width: "16px", height: "16px", accentColor: "var(--color-accent)", flexShrink: 0 }}
                    />
                    <span style={{ fontSize: "0.88rem" }}>
                      None of these work — please contact me
                    </span>
                  </label>
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--color-muted)", marginTop: "0.4rem" }}>
                  Tick everything that works — the more you tick, the better the chance we can fit you in.
                </div>
              </div>

              <div className="form-group">
                <Label>Anything else? (optional)</Label>
                <Textarea
                  rows={2}
                  value={requestForm.notes}
                  onChange={(e) => setRequestForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Preparing for an exam, prefer a specific teacher, etc."
                />
              </div>

              {!clientAccount && (
                <div className="form-group">
                  <Label>Your contact details</Label>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <Input
                      value={requestForm.contactName}
                      onChange={(e) => setRequestForm((f) => ({ ...f, contactName: e.target.value }))}
                      placeholder="Your name *"
                      style={{ flex: 1 }}
                    />
                    <Input
                      value={requestForm.contactPhone}
                      onChange={(e) => setRequestForm((f) => ({ ...f, contactPhone: e.target.value }))}
                      placeholder="Phone"
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowRequest(false)}>Cancel</Button>
                <Button onClick={handleSendRequest} disabled={sendingRequest}>
                  {sendingRequest ? "Sending…" : "Send request"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Week nav */}
      <div className="week-nav">
        <button className="btn btn-outline btn-sm" onClick={() => setWeekOffset(weekOffset - 1)}>← Prev</button>
        <span className="week-nav-label">{format(weekStart, "MMM d")} – {format(weekEnd, "MMM d")}</span>
        <button className="btn btn-outline btn-sm" onClick={() => setWeekOffset(weekOffset + 1)}>Next →</button>
      </div>

      {slots.length === 0 && (
        <p style={{ color: "var(--color-muted)", fontSize: "1rem", marginTop: "1.5rem" }}>
          No lessons scheduled for this week. Try checking the next week.
        </p>
      )}

      <ul style={{listStyle:'none',padding:0,margin:0}}>
        {slots.map((slot, index) => {
          const remainingSpots = slot.capacity - (slot.bookings?.length || 0);
          const isFull = remainingSpots <= 0;
          const isSelected = selectedSlot?.id === slot.id;
          const showDayHeading = index === 0 || slot.date !== slots[index - 1].date;
          const isPrivate = slot.classType !== "Workshop" && slot.classType !== "Custom";
          const isCollapsed = collapsedDays.has(slot.date);
          return (
            <React.Fragment key={slot.id}>
              {showDayHeading && (
                <h3 className="day-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>{format(parseISO(slot.date), "EEEE, MMM d")}</span>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => toggleDay(slot.date)}
                    style={{ fontSize: "0.75rem", padding: "2px 10px" }}
                  >
                    {isCollapsed ? "Show" : "Hide"}
                  </button>
                </h3>
              )}

              {/* Slot card */}
              {!isCollapsed && <li style={{marginBottom:'0.5rem'}}>
                <div className="booking-slot-card">
                  <div className="booking-slot-date">
                    <div className="day-name">{format(parseISO(slot.date), "EEE")}</div>
                    <div className="day-num">{format(parseISO(slot.date), "d")}</div>
                  </div>
                  <div className="booking-slot-info">
                    <div className="booking-slot-name">{slot.customClassName || slot.classType}</div>
                    <div className="booking-slot-meta">{slot.time}{slot.duration ? ` · ${slot.duration} min` : ''} · {slot.teachers?.length ? slot.teachers.join(" or ") : slot.teacher || 'Any teacher'} · {slot.location}{slot.price ? ` · R${slot.price}/pp` : ''}</div>
                    {isFull
                      ? <span className="spots-full">Full</span>
                      : slot.capacity > 1
                      ? <span className="spots-left">{remainingSpots} of {slot.capacity} spots left</span>
                      : <span className="spots-left">Available</span>
                    }
                  </div>
                  {!isFull && (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        if (isSlotWithinBookingWindow(slot.date, slot.time)) {
                          loadStudentsOnce();
                          setSelectedSlot(slot);
                        }
                      }}
                    >
                      Book
                    </button>
                  )}
                </div>

                {/* Booking form inline */}
                {isSelected && (
                  <div className="card" style={{marginTop:'0.5rem'}}>
                    <h4 style={{margin:'0 0 0.75rem',color:'var(--color-text)'}}>Booking for: {slot.date} @ {slot.time}</h4>
                    <p style={{fontSize:'0.85rem',color:'var(--color-muted)',margin:'0 0 0.75rem'}}>
                      Class Type: {slot.classType?.toUpperCase()} · Capacity: {slot.capacity} · Spots Left: {remainingSpots}
                    </p>

                    {/* Solo / Duo toggle — private slots only */}
                    {isPrivate && (
                      <div className="segmented" style={{marginBottom:'0.75rem'}}>
                        {["solo", "duo"].map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setBookingType(t)}
                            className={bookingType === t ? 'active' : ''}
                          >
                            {t === "solo" ? "Solo" : "Duo (2 dancers)"}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Dancer rows */}
                    {[0, ...(isPrivate && bookingType === "duo" ? [1] : [])].map((idx) => (
                      <div key={idx} style={{ position: "relative", marginBottom: "0.5rem" }}>
                        {bookingType === "duo" && (
                          <div style={{ fontSize: "0.8rem", color: "var(--color-muted)", marginBottom: "0.25rem" }}>Dancer {idx + 1}</div>
                        )}
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                          <input
                            type="text"
                            placeholder="First name"
                            value={dancers[idx].name}
                            onChange={(e) => updateDancer(idx, "name", e.target.value)}
                            onFocus={() => { if (suggestions[idx].length > 0) setShowSuggestions((p) => p.map((v, i) => i === idx ? true : v)); }}
                            onBlur={() => setTimeout(() => setShowSuggestions((p) => p.map((v, i) => i === idx ? false : v)), 150)}
                            className="form-input"
                            style={{marginBottom:0}}
                            autoComplete="off"
                          />
                          <input
                            type="text"
                            placeholder="Surname"
                            value={dancers[idx].surname}
                            onChange={(e) => updateDancer(idx, "surname", e.target.value)}
                            onFocus={() => { if (suggestions[idx].length > 0) setShowSuggestions((p) => p.map((v, i) => i === idx ? true : v)); }}
                            onBlur={() => setTimeout(() => setShowSuggestions((p) => p.map((v, i) => i === idx ? false : v)), 150)}
                            className="form-input"
                            style={{marginBottom:0}}
                            autoComplete="off"
                          />
                        </div>
                        {dancers[idx].studentId && (
                          <div style={{ fontSize: "0.78rem", color: "var(--color-success)", marginTop: "0.2rem" }}>✓ Matched to student record</div>
                        )}
                        {showSuggestions[idx] && suggestions[idx].length > 0 && (
                          <div className="autocomplete-dropdown">
                            {suggestions[idx].map((st) => (
                              <div
                                key={st.id}
                                onMouseDown={() => pickStudent(idx, st)}
                                className="autocomplete-item"
                                style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}
                              >
                                <span>{st.name} {st.surname}</span>
                                {clientAccount && st.accountId === clientAccount.id && (
                                  <span style={{ fontSize: "0.7rem", color: "var(--color-accent)", marginLeft: "0.5rem" }}>your student</span>
                                )}
                              </div>
                            ))}
                            <div className="autocomplete-hint">
                              Not listed? Keep typing to book as a new student.
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    {isPrivate && (
                      <>
                        <select
                          value={reason}
                          onChange={(e) => { setReason(e.target.value); setCustomReason(""); }}
                          className="form-input"
                          style={{marginBottom:'0.5rem'}}
                        >
                          <option value="">Select reason for class...</option>
                          {CLASS_REASONS.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                        {reason === "Other" && (
                          <input
                            type="text"
                            placeholder="Please describe your reason..."
                            value={customReason}
                            onChange={(e) => setCustomReason(e.target.value)}
                            className="form-input"
                            style={{marginBottom:'0.5rem'}}
                          />
                        )}
                      </>
                    )}
                    <div style={{display:'flex',gap:'0.5rem'}}>
                      <button
                        className="btn btn-primary"
                        onClick={handleBook}
                        disabled={isBooking || (isPrivate && !reason)}
                      >
                        {isBooking ? "Booking..." : "Confirm Booking"}
                      </button>
                      <button className="btn btn-outline" onClick={() => setSelectedSlot(null)}>Cancel</button>
                    </div>
                  </div>
                )}

                {confirmedBooking?.id === slot.id && (
                  <div style={{ backgroundColor: "color-mix(in oklch, var(--color-success) 12%, var(--color-bg))", border: "1px solid var(--color-success)", borderRadius: "var(--radius-sm)", padding: "1rem", marginTop: "0.5rem" }}>
                    <strong style={{color:'var(--color-text)'}}>Booking confirmed!</strong>
                    <p style={{ margin: "0.5rem 0", fontSize: "0.9rem", color: "var(--color-muted)" }}>
                      {slot.customClassName || slot.classType} on {format(parseISO(slot.date), "EEEE, MMM d")} at {slot.time}
                    </p>
                    <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap'}}>
                      <a
                        href={buildGoogleCalendarUrl(slot)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm"
                      >
                        Add to Google Calendar
                      </a>
                      <button className="btn btn-outline btn-sm" onClick={() => setConfirmedBooking(null)}>
                        Dismiss
                      </button>
                    </div>
                  </div>
                )}
              </li>}
            </React.Fragment>
          );
        })}
      </ul>
    </div>
  );
}


export default BookingPage;
